"use client"

import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import '../../assets/styles/scss/_question_preference_modal.scss'
import logo from '../../assets/img/questionaire_preference_logo.png'
import loader from '../../assets/loading/loading.json'
import {useEffect, useState} from "react";
import finishLoader from '../../assets/img/question_preference/finish_loader.json'
import {Player} from "@lottiefiles/react-lottie-player";
import {usePostHog, useFeatureFlagVariantKey} from "posthog-js/react";
import {filterForScenario} from "./scenarioFilter"
import {useConfig} from "../ConfigProvider";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function FinishDisplay({handleCloseModal}) {
    return (<div className="d-flex flex-column justify-content-center ">
        <Player
            src={finishLoader}
            className="question-finish-loader"
            loop
            autoplay
        />
        <div className="d-flex justify-content-center mt-5">
            <button onClick={handleCloseModal} className="question-preference-modal-button-btn">Finish</button>
        </div>
    </div>)
}

function TextDisplay({question}) {
    return <div dangerouslySetInnerHTML={{__html: question?.title.html}}
                className="mb-5 text-center question-preference-modal-description"/>
}

function ButtonDisplay({question, currentIndex, showFinish, handleBack, handleSkip}) {
    if (showFinish) {
        return null;
    }

    if (question?.type === "text") {
        return <div className="d-flex justify-content-center question-preference-modal-button-container">
            <button
                className="question-preference-modal-button-btn"
                onClick={handleSkip}
                style={{background: 'gray'}}>
                Continue
            </button>
        </div>
    }

    return (question?.skippable) && (
        <div className="question-preference-modal-button-container">
            {currentIndex > 0 && (
                <button
                    className="question-preference-modal-button-btn"
                    onClick={handleBack}
                    style={{background: 'gray'}}
                >
                    Back
                </button>
            )}

            {question?.skippable && (
                <button
                    className="question-preference-modal-button-btn"
                    onClick={handleSkip}
                >
                    Skip for Now
                </button>
            )}
        </div>)

}


export default function QuestionnaireModal(
    {
        questionaireModal,
        setQuestionaireModal,
        questions,
        email,
        campaignName,
        edit = false,
        setCurrentStep,
        currentStep
    }) {
    const postHog = usePostHog()
    const scenario = useFeatureFlagVariantKey('promo-page-conversions')
    const config = useConfig();

    let [currentIndex, setCurrentIndex] = useState(0);
    let [question, setQuestion] = useState(null);
    let [selectedAnswers, setSelectedAnswers] = useState([]);
    let [isFinish, setIsFinish] = useState(false);
    let [isLoading, setIsLoading] = useState(false);
    const [preferences, setPreferences] = useState({});

    const getUserPreferences = async (email) => {
        if (!config) return;
        const res = await fetch(`${config.siteApi.apiUrl}/crm/user?email=${encodeURIComponent(email)}`);

        if (res.ok) {
            let data = await res.json();
            setPreferences(data.data.preference);
        }
    };

    const allQuestions = React.useMemo(() => {
        if (questions) {
            console.log(`Running with scenario ${scenario}`)
            let filteredQuestions = filterForScenario(questions, scenario);
            if (edit) {
                filteredQuestions = filteredQuestions.filter(q => q.type !== 'text');
            }
            return filteredQuestions;
        }
    }, [questions, scenario])

    useEffect(() => {
        if (!allQuestions) {
            return;
        }

        edit && getUserPreferences(email);
        setQuestion(allQuestions[0]);
    }, [allQuestions])


    useEffect(() => {
        // reset selected answers when question changes
        setSelectedAnswers([]);
    }, [currentIndex]);

    useEffect(() => {
        const contentContainer = document.querySelector('.question-preference-modal-content-container');
        if (contentContainer) {
            contentContainer.scrollTop = 0;
        }
    }, [question]);

    function handleSelectAnswer(value) {
        if (!question.multiple) {
            // for single selection question, immediately proceed to next question
            submitAnswer([value]);
        } else {
            // for multiple selection question, update the selected answers
            setSelectedAnswers(prev => {
                const isAlreadySelected = prev.includes(value);

                if (isAlreadySelected) {
                    // remove the value if already selected
                    return prev.filter(item => item !== value);
                } else {
                    // add the value if not yet selected
                    if (question.limit && prev.length >= question.limit) {
                        // if reached the limit, replace the oldest selection
                        const newSelections = [...prev];
                        newSelections.shift(); // remove the first item
                        return [...newSelections, value];
                    } else {
                        // otherwise add to selections
                        return [...prev, value];
                    }
                }
            });
        }
    }

    function submitAnswer(answers = selectedAnswers) {
        if (!config) return;
        setIsLoading(true);

        setTimeout(() => {
            const value = question.multiple ? JSON.stringify(answers) : answers[0];

            fetch(`${config.siteApi.apiUrl}/crm/user`, {
                method: 'POST',
                body: JSON.stringify({
                    email,
                    name: question.key,
                    value,
                    campaignName,
                    lastPreference: currentIndex === allQuestions.length - 1
                })
            });

            if (currentIndex < allQuestions.length - 1) {
                setCurrentIndex(prevIndex => {
                    const newIndex = prevIndex + 1;
                    setQuestion(allQuestions[newIndex]);
                    return newIndex;
                });
            } else {
                setIsFinish(true);
            }

            setIsLoading(false);
        }, 1000);
    }


    function handleSkip() {
        if (currentIndex < allQuestions.length - 1) {
            setCurrentIndex(prevIndex => {
                const newIndex = prevIndex + 1;
                setQuestion(allQuestions[newIndex]);
                return newIndex;
            });
        } else {
            // setQuestionaireModal(false);
            setIsFinish(true);
        }
    }

    function handleBack() {
        if (currentIndex > 0) {
            setCurrentIndex(prevIndex => {
                const newIndex = prevIndex - 1;
                setQuestion(allQuestions[newIndex]);
                return newIndex;
            });
        }
    }

    // auto-proceed to next question once the required number of selections is reached
    useEffect(() => {
        if (question?.multiple && question.limit && selectedAnswers.length === question.limit) {
            // add a small delay to allow user to see their selection before proceeding
            const timer = setTimeout(() => {
                submitAnswer();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [selectedAnswers, question]);

    function handleCloseModal() {
        setQuestionaireModal(false);
        setIsFinish(false);
        setCurrentIndex(0);
        setQuestion(allQuestions[0]);
        // update the how to user show deal modal
        (setCurrentStep && currentStep) && setCurrentStep(currentStep + 1);
    }

    const m = React.useMemo(() => {
        let showOptions = false;
        let showText = false;
        let showFinish = false;

        if (isFinish) {
            showFinish = true;
        } else if (question?.type === 'text') {
            showText = true;
            showOptions = false;
        } else if (question?.options && !isFinish) {
            showOptions = true
        }

        return {showOptions, showFinish, showText};
    }, [isFinish, question, allQuestions])

    const {showOptions, showText, showFinish} = m

    return (
        <Dialog
            fullScreen
            open={questionaireModal}
            onClose={() => setQuestionaireModal(false)}
            TransitionComponent={Transition}
        >
            {isLoading && (
                <div className="question-loader-backdrop">
                    <Player
                        src={loader}
                        className="question-finish-loader"
                        style={{height: "200px", width: "200px"}}
                        loop
                        autoplay
                    />
                </div>
            )}
            <div className="question-preference-modal-wrapper">
                <div className="question-preference-modal-logo-container">
                    <img className="question-preference-modal-logo-img" src={logo?.src}
                         alt={'question-preference-modal-logo'}/>
                </div>
                <div className="question-preference-modal-description-container">
                    {!showFinish &&
                        <>
                            <p className="text-center question-preference-modal-description-subHead">
                                {question?.heading || 'Personalize Your Experience'}
                            </p>
                        </>
                    }
                    {showOptions && <div dangerouslySetInnerHTML={{__html: question?.title.html}}
                                         className="text-center question-preference-modal-description"/>
                    }
                    {showFinish &&
                        <div className="text-center question-preference-modal-description">
                            <h1>Hooray!</h1>
                            <h2>Your Preferences Are Saved</h2>
                            <p className="mt-2">Your discount preferences have been locked in! 🎯</p>
                            <p className="mt-2">Keep an eye on your inbox – exclusive deals and offers are heading
                                your way. 📩💸</p>
                        </div>
                    }
                </div>
                <div className={showText ? "" : "question-preference-modal-content-container"}>
                    <div
                        className={question?.options[0]?.image ? 'question-preference-modal-content-container-div-with-images' : 'question-preference-modal-content-container-div'}>
                        {showFinish &&
                            <FinishDisplay handleCloseModal={handleCloseModal}/>}
                        {showOptions && question?.options.map((item) => {
                            const isSelected =
                                (selectedAnswers.length || !preferences[question.key]) ? selectedAnswers.includes(item.value) :
                                    (question.type === 'multiple' ? (preferences[question.key] && JSON.parse(preferences[question.key])?.includes(item.value)) : preferences[question.key] === item.value);
                            return (
                                <div
                                    className={`${item?.image?.url ? 'question-preference-modal-content-answer-withImages' : 'question-preference-modal-content-answer'} ${isSelected ? 'question-preference-modal-content-answer-selected' : ''}`}
                                    key={item.value}
                                    onClick={() => handleSelectAnswer(item.value)}
                                >
                                    <div className="question-preference-modal-answer-wrapper">
                                        {item?.image && <img className="question-preference-modal-content-answer-img"
                                                             src={item?.image?.url} alt="question-image"/>}
                                        <span
                                            className="text-center question-preference-modal-content-answer-text">{item.label}</span>
                                    </div>
                                </div>
                            );
                        })
                        }
                        {showText && <TextDisplay question={question}/>}
                    </div>

                </div>
                <ButtonDisplay question={question} handleBack={handleBack}
                               handleSkip={handleSkip} currentIndex={currentIndex} showFinish={showFinish}/>
            </div>
        </Dialog>
    )
}
